import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/contexts";
import { showToast } from "@/hooks/useToast";
import { Logo } from "@/components/core/logo";

export const Route = createFileRoute("/auth/google/callback")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { saveAuth } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const hasProcessedRef = useRef(false);
  const processingCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      if (hasProcessedRef.current) {
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");
      const code = params.get("code");
      const state = params.get("state");
      const error = params.get("error");
      const requiresPhoneVerification = params.get("requiresPhoneVerification") === "true";

      // Se já está processando este code, não processa novamente
      if (code && processingCodeRef.current === code) {
        return;
      }

      if (code) {
        processingCodeRef.current = code;
      }

      hasProcessedRef.current = true;

      try {

        // Se não há code, token ou error, não há nada para processar (pode ser uma navegação normal)
        if (!code && !token && !error) {
          setIsProcessing(false);
          return;
        }

        // Se recebeu code do Google, envia para o backend processar
        if (code) {
          try {
            const apiUrl = import.meta.env.VITE_API_URL || "";
            const redirectUri = `${window.location.origin}/auth/google/callback`;
            // Remove /api do final se existir, pois vamos adicionar o caminho completo
            const baseUrl = apiUrl.replace(/\/api\/?$/, '');
            const endpointUrl = `${baseUrl}/api/auth/google/process`;

            // Cria um AbortController para timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
              controller.abort();
            }, 30000); // 30 segundos

            let response: Response;
            try {
              response = await fetch(endpointUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code, state, redirectUri }),
                signal: controller.signal,
              });
              clearTimeout(timeoutId);
            } catch (fetchError: any) {
              clearTimeout(timeoutId);
              if (fetchError.name === 'AbortError') {
                showToast.error("Tempo de espera excedido. Tente novamente.", { duration: 5000 });
              } else {
                showToast.error("Erro ao conectar com o servidor. Verifique sua conexão.", { duration: 5000 });
              }
              navigate({ to: "/auth/identify" });
              return;
            }

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ message: 'Erro ao processar autenticação' }));
              showToast.error(errorData.message || 'Erro ao processar autenticação', { duration: 5000 });
              navigate({ to: "/auth/identify" });
              return;
            }

            const authData = await response.json();

            // Se recebeu tempToken, é um pré-cadastro do Google que precisa ser completado
            if (authData.tempToken) {
              showToast.success("Bem-vindo! Complete seus dados para ativar a conta.");
              navigate({
                to: "/auth/complete-profile",
                search: {
                  tempToken: authData.tempToken,
                  nome: authData.user?.nome || "",
                  email: authData.user?.email || "",
                  foto: authData.user?.foto,
                },
              });
              return;
            }

            // Salva a autenticação apenas se recebeu token
            if (authData.access) {
              saveAuth(authData);
              showToast.success("Login realizado com sucesso!");

              // Redireciona baseado no status de verificação do telefone
              if (authData.requiresPhoneVerification || !authData.user.telefoneVerificado) {
                navigate({
                  to: "/auth/verify-phone",
                  search: { telefone: authData.user.telefone },
                });
              } else {
                navigate({ to: "/app" });
              }
              return;
            }

            // Sem token: precisa verificar algo antes de entrar
            if (authData.requiresPhoneVerification || !authData.user.telefoneVerificado) {
              showToast.success("Telefone verificado! Redirecionando para verificação...");
              navigate({
                to: "/auth/verify-phone",
                search: { telefone: authData.user.telefone },
              });
              return;
            }

            if (authData.requiresEmailVerification || !authData.user.emailVerificado) {
              showToast.error("Verifique seu e-mail para ativar a conta antes de entrar.");
              navigate({ to: "/auth/identify" });
              return;
            }

            // Fallback
            showToast.error("Não foi possível concluir o login");
            navigate({ to: "/auth/identify" });
            return;
          } catch (err) {
            console.error("Erro ao processar callback do Google:", err);
            showToast.error(err instanceof Error ? err.message : "Falha ao processar autenticação", { duration: 5000 });
            navigate({ to: "/auth/identify" });
            return;
          }
        }

        if (error) {
          showToast.error(decodeURIComponent(error), { duration: 5000 });
          navigate({ to: "/auth/identify" });
          return;
        }

        if (!token) {
          showToast.error("Token não recebido do servidor", { duration: 5000 });
          navigate({ to: "/auth/identify" });
          return;
        }

        // Busca os dados do usuário usando o token
        try {
          const apiUrl = import.meta.env.VITE_API_URL || "";
          const response = await fetch(`${apiUrl}/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error("Falha ao buscar dados do usuário");
          }

          const user = await response.json();

          // Salva a autenticação
          saveAuth({
            access: token,
            user,
            requiresPhoneVerification: requiresPhoneVerification || undefined,
          });

          showToast.success("Login realizado com sucesso!");

          // Redireciona baseado no status de verificação do telefone
          if (requiresPhoneVerification || !user.telefoneVerificado) {
            navigate({
              to: "/auth/verify-phone",
              search: { telefone: user.telefone },
            });
          } else {
            navigate({ to: "/app" });
          }
        } catch (err) {
          console.error("Erro ao buscar dados do usuário:", err);
          showToast.error("Falha ao processar autenticação", { duration: 5000 });
          navigate({ to: "/auth/identify" });
        }
      } catch (err) {
        console.error("Erro ao processar callback:", err);
        showToast.error("Erro ao processar callback do Google", { duration: 5000 });
        navigate({ to: "/auth/identify" });
      } finally {
        setIsProcessing(false);
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executa apenas uma vez na montagem

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url("/authentication-bg.svg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-4xl shadow-xl p-8">
          <div className="flex items-center justify-center mb-8">
            <Logo className="h-12" isTraditionalLogo />
          </div>

          <div className="text-center space-y-6">
            {isProcessing ? (
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary"></div>
                </div>
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Conectando com Google
                  </h2>
                  <p className="text-sm text-gray-600 px-4">
                    Aguarde enquanto processamos sua autenticação...
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-gray-100 p-3">
                    <svg
                      className="w-6 h-6 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Processamento concluído
                  </h2>
                  <p className="text-sm text-gray-600">
                    Redirecionando...
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
